/*
 * section-product.js — Phase 3 PDP dedicated script.
 * Loaded conditionally in layout/theme.liquid when template.name == 'product'.
 * Per-plan owners: skeleton = plan 03-02, gallery = 03-03, tabs+hash = 03-04, bundle = 03-05.
 *
 * Strict mode + IIFE wrapper: no global leaks. Defer-loaded per theme.liquid.
 */
(function () {
  'use strict';

  // Early-exit if not on a product page (defensive — theme.liquid conditional already
  // gates this, but a CSP-safe re-check prevents accidental global execution if the
  // file is ever inlined elsewhere).
  if (document.body && document.body.dataset && document.body.dataset.template !== undefined) {
    if (document.body.dataset.template !== 'product') return;
  }

  // plan 03-03: gallery thumb click-to-swap
  (function initGallery() {
    const gallery = document.querySelector('.pdp-gallery');
    if (!gallery) return;
    const thumbs = gallery.querySelectorAll('.pdp-gallery__thumb');
    const mainImg = gallery.querySelector('.pdp-gallery__main img');
    if (!thumbs.length || !mainImg) return;

    function activateThumb(thumb) {
      const srcLarge = thumb.getAttribute('data-src-large');
      const srcsetLarge = thumb.getAttribute('data-srcset-large');
      if (!srcLarge) return;
      mainImg.src = srcLarge;
      if (srcsetLarge) mainImg.srcset = srcsetLarge;
      // Drop priority hints after first user interaction; image is already loaded
      mainImg.removeAttribute('fetchpriority');
      thumbs.forEach((t) => {
        t.classList.toggle('pdp-gallery__thumb--active', t === thumb);
        t.setAttribute('aria-pressed', t === thumb ? 'true' : 'false');
      });
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => activateThumb(thumb));
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activateThumb(thumb);
        }
      });
    });
  })();

  // plan 03-03: quantity stepper (- / + buttons drive the numeric input)
  (function initQtyStepper() {
    document.querySelectorAll('.qty').forEach((qty) => {
      const input = qty.querySelector('.qty__input');
      if (!input) return;
      qty.querySelectorAll('.qty__btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const dir = btn.getAttribute('data-qty');
          const min = parseInt(input.min || '1', 10);
          let v = parseInt(input.value || '1', 10);
          if (Number.isNaN(v)) v = min;
          if (dir === 'plus') v = v + 1;
          if (dir === 'minus') v = Math.max(min, v - 1);
          input.value = String(v);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  })();

  // plan 03-04: tabs + URL-hash handler
  (function initTabs() {
    const tabs = document.querySelector('.pdp-tabs');
    if (!tabs) return;

    const isDesktop = () => window.matchMedia('(min-width: 768px)').matches;
    const tabButtons = tabs.querySelectorAll('.pdp-tabs__tab');
    const panels = tabs.querySelectorAll('.pdp-tabs__panel');

    function activateTab(tabId, opts) {
      opts = opts || {};
      tabButtons.forEach((b) => {
        const match = b.dataset.tab === tabId;
        b.classList.toggle('pdp-tabs__tab--active', match);
        b.setAttribute('aria-selected', match ? 'true' : 'false');
      });
      panels.forEach((p) => {
        const match = p.dataset.panel === tabId;
        p.classList.toggle('pdp-tabs__panel--active', match);
        if (isDesktop()) {
          // Desktop: keep all <details> open for layout; visibility via .pdp-tabs__panel--active + CSS
          if (!p.open) p.open = true;
        }
      });
      if (opts.scroll) {
        tabs.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    tabButtons.forEach((b) => {
      b.addEventListener('click', () => activateTab(b.dataset.tab));
    });

    const hashToTab = {
      reviews: 'bewertungen',
      bewertungen: 'bewertungen',
      faq: 'faq',
      lief: 'lief',
      tech: 'tech',
      montage: 'montage',
      desc: 'desc'
    };

    function handleHash() {
      const hash = (location.hash || '').replace('#', '');
      if (!hash) return;
      const tabId = hashToTab[hash];
      if (!tabId) return;
      if (isDesktop()) {
        activateTab(tabId, { scroll: true });
      } else {
        // Mobile: open the matching <details>, scroll into view
        const panel = tabs.querySelector('[data-panel="' + tabId + '"]');
        if (panel) {
          panel.open = true;
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    window.addEventListener('load', handleHash);
    window.addEventListener('hashchange', handleHash);

    // Breakpoint cross listener — re-sync <details open> states at 768px
    window.matchMedia('(min-width: 768px)').addEventListener('change', (e) => {
      if (e.matches) {
        // Entering desktop: first panel active, all details open for layout
        panels.forEach((p, i) => {
          p.open = true;
          p.classList.toggle('pdp-tabs__panel--active', i === 0);
        });
        if (tabButtons[0]) tabButtons[0].classList.add('pdp-tabs__tab--active');
      } else {
        // Entering mobile: close all details except first
        panels.forEach((p, i) => {
          p.open = i === 0;
          p.classList.remove('pdp-tabs__panel--active');
        });
      }
    });
  })();

  // plan 03-05: bundle single-POST AJAX handler
  // Pattern per RESEARCH §Single-POST bundle cart-add (lines 466-563).
  // - Fetches /cart/add.js with body { items: [{id, quantity}, ...] } in ONE POST
  // - On success: dual-dispatch cart-update so both Phase 1.1 cart-notification
  //   (subscribed to PUB_SUB_EVENTS.cartUpdate via assets/cart-notification.js)
  //   AND any future generic listeners (cart:refresh CustomEvent) pick it up.
  //   product-form.js publishes the same PUB_SUB_EVENTS.cartUpdate on standard
  //   single-item adds (assets/product-form.js:71), so cart-notification's
  //   subscriber is the canonical refresh path.
  // - On error: shows hardcoded German message in .pdp-bundle__error wrapper
  //   (T-03-05-03 mitigation — server response text is NOT rendered into DOM).
  (function initBundle() {
    const form = document.querySelector('.pdp-bundle__form');
    if (!form) return;
    const errorEl = form.querySelector('.pdp-bundle__error');
    const cta = form.querySelector('.pdp-bundle__cta');
    const ctaSpan = cta ? cta.querySelector('span') : null;
    const originalCtaLabel = cta ? (ctaSpan ? ctaSpan.textContent : cta.textContent) : '';

    function setCtaLabel(text) {
      if (!cta) return;
      if (ctaSpan) {
        ctaSpan.textContent = text;
      } else {
        cta.textContent = text;
      }
    }

    async function addBundleToCart(items) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
        ? window.Shopify.routes.root
        : '/';
      const url = root + 'cart/add.js';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ items: items })
      });
      if (!response.ok) {
        let msg = 'HTTP ' + response.status;
        try {
          const err = await response.json();
          if (err && err.description) msg = err.description;
        } catch (_e) { /* swallow — we use hardcoded fallback below */ }
        const e = new Error(msg);
        e.httpStatus = response.status;
        throw e;
      }
      return response.json();
    }

    function dispatchCartRefresh(result) {
      // 1) Generic CustomEvent for any future listener
      try {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { source: 'pdp-bundle', result: result }
          })
        );
      } catch (_e) { /* CustomEvent unsupported — ignore */ }

      // 2) Craft pubsub: this is what cart-notification.js subscribes to.
      //    PUB_SUB_EVENTS lives in assets/constants.js as a global; publish() in
      //    assets/pubsub.js. Both load before section-product.js per layout/theme.liquid.
      if (typeof window.publish === 'function' &&
          typeof window.PUB_SUB_EVENTS !== 'undefined' &&
          window.PUB_SUB_EVENTS.cartUpdate) {
        try {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'pdp-bundle',
            cartData: result
          });
        } catch (_e) { /* publish errors are non-fatal */ }
      }
    }

    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      if (form.dataset.disabled === 'true') return;
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      const items = [];
      form.querySelectorAll('[data-bundle-item]').forEach(function (row) {
        const idInput = row.querySelector('input[name$="[id]"]');
        const qtyInput = row.querySelector('input[name$="[quantity]"]');
        if (idInput && idInput.value) {
          const id = parseInt(idInput.value, 10);
          const qty = parseInt((qtyInput && qtyInput.value) || '1', 10);
          if (!Number.isNaN(id) && id > 0 && qty > 0) {
            items.push({ id: id, quantity: qty });
          }
        }
      });

      if (items.length === 0) return;

      if (cta) {
        cta.disabled = true;
        cta.setAttribute('aria-disabled', 'true');
        setCtaLabel('Wird hinzugefügt …');
      }

      try {
        const result = await addBundleToCart(items);
        dispatchCartRefresh(result);
        if (cta) {
          cta.disabled = false;
          cta.removeAttribute('aria-disabled');
          setCtaLabel(originalCtaLabel || 'Alle in den Warenkorb');
        }
      } catch (_err) {
        if (cta) {
          cta.disabled = false;
          cta.removeAttribute('aria-disabled');
          setCtaLabel(originalCtaLabel || 'Alle in den Warenkorb');
        }
        if (errorEl) {
          errorEl.textContent = 'Hinzufügen fehlgeschlagen. Bitte erneut versuchen.';
          errorEl.hidden = false;
        }
      }
    });
  })();

  // Deferred (post-launch per D-19 + RESEARCH recommendation):
  //  - Sticky bar intersection observer (hide when main ATC visible)
  //  - Judge.me widget rebind on first Bewertungen tab activation

})();
